import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { Shift, ShiftType } from '../../types/schedule';
import { Modal, Button, Select } from '../common';
import { toast } from 'react-hot-toast';
import { useConfirmation } from '../../hooks/confirmationContext';
import { getTeamMembersWithUserInfo } from '../../lib/api/teamService';
import { shiftService } from '../../lib/api/services/shiftService';

interface ShiftDetailModalProps {
  shift: (Shift & { provider_name?: string }) | null;
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  onShiftUpdated?: () => void;
  readOnly?: boolean;
}

interface TeamMember {
  user_id: string;
  full_name?: string;
  shift_type: 'days_only' | 'nights_only' | 'both';
}

const ShiftDetailModal: React.FC<ShiftDetailModalProps> = ({
  shift,
  isOpen,
  onClose,
  teamId,
  onShiftUpdated,
  readOnly = false,
}) => {
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const { showConfirmation } = useConfirmation();
  
  // Define fetchTeamMembers with useCallback before it's used in useEffect
  const fetchTeamMembers = useCallback(async () => {
    setIsLoadingMembers(true);
    try {
      const members = await getTeamMembersWithUserInfo(teamId);
      if (members) {
        // Transform to expected format
        const formattedMembers = members.map(member => ({
          user_id: member.user_id,
          full_name: member.user?.full_name || 'Unknown User',
          shift_type: member.shift_type as 'days_only' | 'nights_only' | 'both'
        }));
        setTeamMembers(formattedMembers);
      }
    } catch (fetchError) {
      console.error('Error fetching team members:', fetchError);
      toast.error('Failed to load team members');
    } finally {
      setIsLoadingMembers(false);
    }
  }, [teamId]);
  
  // Fetch team members
  useEffect(() => {
    if (isOpen && teamId) {
      fetchTeamMembers();
    }
  }, [isOpen, teamId, fetchTeamMembers]);
  
  // Reset selected provider when the shift changes
  useEffect(() => {
    if (shift) {
      setSelectedProviderId(shift.provider_id || null);
    }
  }, [shift]);
  
  const assignProvider = async (shiftId: string, providerId: string | null) => {
    setIsLoading(true);
    try {
      const { data, error } = await shiftService.assignProviderToShift(shiftId, providerId);
      if (error) throw error;
      return data !== null;
    } catch (assignError) {
      console.error('Error assigning provider:', assignError);
      throw assignError;
    } finally {
      setIsLoading(false);
    }
  };
  
  const deleteShift = async (shiftId: string) => {
    setIsLoading(true);
    try {
      const { error } = await shiftService.deleteShift(shiftId);
      if (error) throw error;
      return true;
    } catch (deleteError) {
      console.error('Error deleting shift:', deleteError);
      throw deleteError;
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!shift) return null;
  
  const handleAssignProvider = async () => {
    if (!shift || isLoading) return;
    
    try {
      await assignProvider(shift.id, selectedProviderId);
      toast.success('Provider assigned successfully');
      
      if (onShiftUpdated) {
        onShiftUpdated();
      }
      onClose();
    } catch (error) {
      console.error('Error assigning provider:', error);
      toast.error('Failed to assign provider. Please try again.');
    }
  };
  
  const handleDeleteShift = async () => {
    if (!shift || isLoading) return;
    
    showConfirmation({
      title: 'Delete Shift',
      message: 'Are you sure you want to delete this shift? This action cannot be undone.',
      confirmText: 'Delete',
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          await deleteShift(shift.id);
          
          toast.success('Shift deleted successfully');
          onClose();
          
          if (onShiftUpdated) {
            onShiftUpdated();
          }
        } catch (error) {
          console.error('Error deleting shift:', error);
          toast.error('Failed to delete shift. Please try again.');
        }
      },
    });
  };
  
  // Filter providers based on shift type eligibility
  const eligibleProviders = teamMembers.filter(member => {
    if (shift.shift_type === 'day') {
      return member.shift_type === 'days_only' || member.shift_type === 'both';
    } else {
      return member.shift_type === 'nights_only' || member.shift_type === 'both';
    }
  });
  
  const providerOptions = eligibleProviders.map(provider => ({
    value: provider.user_id,
    label: provider.full_name || provider.user_id,
  }));
  
  // Add an "Unassigned" option
  const options = [
    { value: '', label: 'Unassigned' },
    ...providerOptions,
  ];
  
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'EEEE, MMMM d, yyyy');
    } catch (formatError) {
      console.error('Error formatting date:', formatError);
      return dateString;
    }
  };
  
  const getShiftTypeLabel = (type: ShiftType) => {
    return type === 'day' ? 'Day Shift' : 'Night Shift';
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Shift Details">
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <div className="font-medium text-gray-500">Date</div>
          <div className="text-gray-900">{formatDate(shift.date)}</div>
        </div>
        
        <div className="space-y-2">
          <div className="font-medium text-gray-500">Shift Type</div>
          <div className="text-gray-900">{getShiftTypeLabel(shift.shift_type as ShiftType)}</div>
        </div>
        
        <div className="space-y-2">
          <div className="font-medium text-gray-500">Provider</div>
          {readOnly ? (
            <div className="text-gray-900">
              {shift.provider_name || 'Unassigned'}
            </div>
          ) : (
            <Select
              value={selectedProviderId || ''}
              onChange={e => setSelectedProviderId(e.target.value || null)}
              options={options}
              disabled={isLoadingMembers || isLoading}
              label="Select a provider"
            />
          )}
        </div>
      </div>
      
      <div className="flex justify-end gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
        {!readOnly && (
          <Button
            variant="danger"
            className="mr-auto"
            onClick={handleDeleteShift}
            disabled={isLoading}
          >
            Delete Shift
          </Button>
        )}
        
        <Button variant="secondary" onClick={onClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        
        {!readOnly && (
          <Button
            variant="primary"
            onClick={handleAssignProvider}
            isLoading={isLoading}
            disabled={shift.provider_id === selectedProviderId}
          >
            Assign Provider
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default ShiftDetailModal; 